import api from './api';
import { resolvePhotoUrl } from './photoUrl';

/** A storage PUT that was answered, with a status that is not a success. It
 *  reached somebody: unlike a fetch that fails outright, it is not "no
 *  connection". */
export class StorageHttpError extends Error {
  constructor(status) {
    super(`S3 upload failed (HTTP ${status})`);
    this.status = status;
  }
}

/** Why one photo did not land. `status` and `code` are the API's answer,
 *  where it gave one; `unreachable` says nobody answered at all -- a request
 *  with no response, or a storage PUT the network never delivered -- which a
 *  caller can wait out rather than count as a refusal. */
export function failureOf(error) {
  return {
    status: error?.response?.status ?? null,
    code: error?.response?.data?.code ?? null,
    unreachable: !error?.response && !(error instanceof StorageHttpError),
  };
}

// The URL a presign hands out is S3's in the cloud. On the desktop it names
// http://127.0.0.1:<port>, which on a phone is the phone: re-based onto the
// server this client talks to, keeping its ?exp&sig (utils/photoUrl.js).
// Voice notes go up the same way (utils/audioNotes.js).
export async function putObject(url, file, type = file.type || 'image/jpeg') {
  const target = resolvePhotoUrl(url);
  const send = () => fetch(target, {
    method:  'PUT',
    body:    file,
    headers: { 'Content-Type': type },
  });
  // Check the HTTP status, and try once more on a failure.
  let res = await send();
  if (!res.ok) {
    res = await send();
    if (!res.ok) throw new StorageHttpError(res.status);
  }
}

// A config only where one is asked for: callers (and their tests) that pass
// none see the same two-argument calls as ever.
const withConfig = (config, headers) => {
  const merged = { ...(config || {}), ...(headers ? { headers: { ...(config?.headers || {}), ...headers } } : {}) };
  return Object.keys(merged).length ? [merged] : [];
};

/**
 * One photo: presign, PUT to storage, then POST /photos.
 *
 * options:
 *   stored          {s3_key, s3_url} of an earlier PUT of this same file: the
 *                   presign and PUT are skipped, and only POST /photos is sent
 *   onStored        called with {s3_key, s3_url} once the PUT has landed and
 *                   before POST /photos, so a caller can keep it for a retry
 *   idempotencyKey  sent with POST /photos: a retry of a registration whose
 *                   answer was lost gets that answer back, not a second row
 *   requestConfig   merged into every API request's config
 *   thumbnail       a small JPEG of the photo (a Blob), put in storage beside
 *                   it for the gallery's cards (photos.thumb_key, 0053). A
 *                   small copy that does not upload is not a failure: the
 *                   photo is registered without it, and shown at full size
 *
 * Resolves {ok: true, photo_id, s3_key, s3_url}, or {ok: false, status, code,
 * unreachable} (failureOf). Never rejects.
 */
export async function uploadOnePhoto({ file, photoType, notes }, uploadBody, taxonName, sourceType, options = {}) {
  const { stored, onStored, idempotencyKey, requestConfig, thumbnail } = options;
  try {
    let where = stored && stored.s3_key && stored.s3_url ? stored : null;
    if (!where) {
      const presignRes = await api.post('/photos/presign', {
        filename:     file.name,
        content_type: file.type || 'image/jpeg',
        photo_type:   photoType,
        taxon_name:   taxonName || '',
        source_type:  sourceType,
        ...(thumbnail ? { with_thumbnail: true } : {}),
      }, ...withConfig(requestConfig));
      const { presign_url, s3_key, s3_url, thumb_presign_url, thumb_key } = presignRes.data;
      await putObject(presign_url, file);
      where = { s3_key, s3_url };
      if (thumbnail && thumb_presign_url && thumb_key) {
        try {
          await putObject(thumb_presign_url, thumbnail, 'image/jpeg');
          where.thumb_key = thumb_key;
        } catch { /* the photo goes without its small copy */ }
      }
      if (onStored) await onStored(where);
    }
    const res = await api.post('/photos', {
      ...uploadBody, photo_type: photoType, photo_source: sourceType,
      s3_url: where.s3_url, s3_key: where.s3_key, notes: notes || null,
      ...(where.thumb_key ? { thumb_key: where.thumb_key } : {}),
    }, ...withConfig(requestConfig, idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : null));
    return { ok: true, photo_id: res?.data?.photo_id ?? null, ...where };
  } catch (e) {
    console.error(`Photo upload failed [${photoType}]:`, e);
    return { ok: false, ...failureOf(e) };
  }
}

export async function uploadPhotos(pendingPhotos, uploadBody, taxonName, sourceType) {
  let succeeded = 0;
  const failed = [];

  for (const { file, photoType, notes } of pendingPhotos) {
    const result = await uploadOnePhoto({ file, photoType, notes }, uploadBody, taxonName, sourceType);
    if (result.ok) {
      succeeded++;
    } else {
      // Why, where the server said: a caller may be able to send it differently.
      failed.push({ photoType, filename: file.name, status: result.status, code: result.code,
                    unreachable: result.unreachable });
    }
  }

  return { succeeded, failed };
}
